import {
    convertMsToString,
    isOFFDutyStatus,
    getAvailabilityDisplaySummary,
    getDisplayObjectHos,
    DISPLAY_CHECKMARK,
    type IAvailabilityDisplayInfo,
    timeSpanToMs,
    getRuleSetById,
    type IHosRuleset
} from "hos-tools";
import type { IHosProcessResult } from "../../../../services/hosProcess";
import DutyStatusLog from "../../../../model/DutyStatusLog";
import { dstCalculator } from "../../../../utils/date";
import { HOURS } from "../../../../constants";

export interface IAvailabilityDisplayItem {
    id: string;
    icon: {
        href: string;
        ariaLabel: string;
    };
    label: string;
    value: string;
    isCheckmark: boolean;
    skipTranslateLabel: boolean;
}

export interface IEwdAvailabilityDisplayItem {
    isResting: boolean;
    restDuration: string;
    realTimeUntil15: string;
    realTimeUntil7: string;
    realTimeUntil24: string;
    realTimeUntilNightRest: string;
    timeUntil15: string;
    timeUntil7: string;
    timeUntil24: string;
    timeUntilNightRest: string;
    dayCycleStarted: string;
    sevenDayCycleStarted: string;
    fourteenDayCycleStarted: string;
    oneNightRestChecks: string;
    twoNightRestChecks: string;
}

export default class AvailabilityDisplayHelper {
    private translatedHourUnit = "";
    private translatedMinuteUnit = "";

    setTranslatedStrings(translatedHourUnit = "h", translatedMinuteUnit = "m"): void {
        this.translatedHourUnit = translatedHourUnit;
        this.translatedMinuteUnit = translatedMinuteUnit;
    }

    getDisplayItems(
        ruleResult: IHosProcessResult,
        ruleSetKey: string,
        currentLog: DutyStatusLog | null,
        daysString: string,
        mode: "HOS" | "EWD" = "HOS",
        currentTime: Date | undefined = undefined,
        translatedHourUnit = "h",
        translatedMinuteUnit = "m",
        translatedDrivingBreakDuration = "{0} Break duration",
        translatedDrivingBreak = "{0} Break",
        translatedDrivingBreakIn = "{0} Break required in"
    ): IAvailabilityDisplayItem[] | IEwdAvailabilityDisplayItem | null {
        this.setTranslatedStrings(translatedHourUnit, translatedMinuteUnit);

        if (mode === "HOS") {
            const hosDisplayObject = getDisplayObjectHos(
                ruleResult,
                ruleSetKey,
                currentLog,
                daysString,
                translatedHourUnit,
                translatedMinuteUnit,
                translatedDrivingBreakDuration,
                translatedDrivingBreak,
                translatedDrivingBreakIn
            );
            const availabilityDisplaySummary = getAvailabilityDisplaySummary(
                hosDisplayObject,
                ruleResult.preciseAvailability
            );
            return this.mapAvailabilityDisplayInfoToItems(availabilityDisplaySummary);
        } else {
            // EWD MODE
            if (ruleResult.ewdAvailability && ruleResult.ewdAvailability.length > 0) {
                const timeUntilMustRestArray = ruleResult.ewdAvailability.map(
                    ewdObject => ewdObject.timeUntilMustRest ? timeSpanToMs(ewdObject.timeUntilMustRest) : 0
                );
                const isResting = !currentLog || isOFFDutyStatus(currentLog.status);
                const firstTimePeriod = ruleResult.ewdAvailability[ruleResult.ewdAvailability.length - 1];
                const restDuration = isResting && firstTimePeriod && firstTimePeriod.rested ?
                    timeSpanToMs(firstTimePeriod.rested) : undefined;
                const minTimeUntil15MinuteBreak = Math.min.apply(Math, [
                    timeUntilMustRestArray[0],
                    timeUntilMustRestArray[1],
                    timeUntilMustRestArray[2]
                ]);
                const ruleset = getRuleSetById(ruleSetKey);
                const restCheckmarks = this.getRestCheckmarksTemplate(ruleResult, ruleset || <IHosRuleset>{});
                const realTimeUntil15 = new Date((currentTime?.getTime() || 0) + minTimeUntil15MinuteBreak);
                const realTimeUntil7 = new Date((currentTime?.getTime() || 0) + timeUntilMustRestArray[3]);
                const realTimeUntil24 = new Date((currentTime?.getTime() || 0) + timeUntilMustRestArray[4]);
                const realTimeUntilNightRest = new Date((currentTime?.getTime() || 0) + timeUntilMustRestArray[5]);

                realTimeUntil15.setSeconds(0);
                realTimeUntil7.setSeconds(0);
                realTimeUntil24.setSeconds(0);
                realTimeUntilNightRest.setSeconds(0);

                const dayCycleStarted = dstCalculator.toLocalDateTime(ruleResult.ewdAvailability[3].startDate as Date);
                const sevenDayCycleStarted = dstCalculator.toLocalDateTime(
                    ruleResult.ewdAvailability[4].startDate as Date
                );
                const fourteenDayCycleStarted = dstCalculator.toLocalDateTime(
                    ruleResult.ewdAvailability[5].startDate as Date
                );

                dayCycleStarted.setSeconds(0);
                sevenDayCycleStarted.setSeconds(0);
                fourteenDayCycleStarted.setSeconds(0);

                return {
                    isResting,
                    restDuration: restDuration !== undefined ? this.getTimeString(restDuration) : "",
                    realTimeUntil15: realTimeUntil15.toLocaleString("en-au", { hour12: false }),
                    realTimeUntil7: realTimeUntil7.toLocaleString("en-au", { hour12: false }),
                    realTimeUntil24: realTimeUntil24.toLocaleString("en-au", { hour12: false }),
                    realTimeUntilNightRest: realTimeUntilNightRest.toLocaleString("en-au", { hour12: false }),
                    timeUntil15: convertMsToString(
                        minTimeUntil15MinuteBreak / HOURS,
                        this.translatedHourUnit,
                        this.translatedMinuteUnit
                    ),
                    timeUntil7: convertMsToString(
                        timeUntilMustRestArray[3] / HOURS,
                        this.translatedHourUnit,
                        this.translatedMinuteUnit
                    ),
                    timeUntil24: convertMsToString(
                        timeUntilMustRestArray[4] / HOURS,
                        this.translatedHourUnit,
                        this.translatedMinuteUnit
                    ),
                    timeUntilNightRest: convertMsToString(
                        timeUntilMustRestArray[5] / HOURS,
                        this.translatedHourUnit,
                        this.translatedMinuteUnit
                    ),
                    dayCycleStarted: dayCycleStarted.toLocaleString("en-au", { hour12: false }),
                    sevenDayCycleStarted: sevenDayCycleStarted.toLocaleString("en-au", { hour12: false }),
                    fourteenDayCycleStarted: fourteenDayCycleStarted.toLocaleString("en-au", { hour12: false }),
                    oneNightRestChecks: restCheckmarks[0],
                    twoNightRestChecks: restCheckmarks[1]
                };
            }
            return null;
        }
    }

    getTimeString(ms: number): string {
        const seconds = ms / 1000;
        const minutes = seconds / 60;
        const hours = minutes / 60;
        const days = hours / 24;
        const rMinutes = ~~(minutes % 60);
        const rHours = ~~(hours % 24);
        const rDays = ~~days;

        return `${ (rDays * 24) + rHours }${ this.translatedHourUnit } ${ rMinutes }${ this.translatedMinuteUnit }`;
    }

    private getRestCheckmarksTemplate(ruleResult: IHosProcessResult, ruleset: IHosRuleset): string[] {
        const templateStrings = ["", ""];

        if (ruleResult.ewdAvailability && ruleset.config.workRestConfig) {
            const oneNightRestChecks = ruleset.config.workRestConfig[5].restPeriod?.nightRestBreakCount || 0;
            const twoNightRestChecks = ruleset.config.workRestConfig[5].restPeriod?.consecutiveNightRestBreakCount || 0;

            const fulfilledOneNightRest = ruleResult.ewdAvailability[5].nightRestBreakCount || 0;
            const unfulfilledOneNightRest = oneNightRestChecks - fulfilledOneNightRest;
            for (let i = 0; i < fulfilledOneNightRest; i++) {
                templateStrings[0] += `<span id="singleNightRest" class="blue">✓</span>`;
            }

            for (let i = 0; i < unfulfilledOneNightRest; i++) {
                templateStrings[0] += `✓`;
            }

            const fulfilledTwoNightRest = ruleResult.ewdAvailability[5].consecutiveNightRestBreakCount || 0;
            const unfulfilledTwoNightRest = twoNightRestChecks - fulfilledTwoNightRest;
            for (let i = 0; i < fulfilledTwoNightRest; i++) {
                templateStrings[1] += `<span id="consecutiveNightRest" class="blue">✓</span>`;
            }

            for (let i = 0; i < unfulfilledTwoNightRest; i++) {
                templateStrings[1] += `✓`;
            }
        }

        return templateStrings;
    }

    private mapAvailabilityDisplayInfoToItems(
        availabilityDisplayInfos: IAvailabilityDisplayInfo[]
    ): IAvailabilityDisplayItem[] {
        return availabilityDisplayInfos.map(info => ({
            id: info.htmlId,
            icon: {
                href: info.icon.href,
                ariaLabel: info.icon.ariaLabel
            },
            label: info.label,
            value: info.value,
            isCheckmark: info.value === DISPLAY_CHECKMARK,
            skipTranslateLabel: info.skipTranslateLabel || false
        }));
    }
}
